import React, { useState } from 'react';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { X, Mail, Lock, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { signIn, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);

  const handleGoogleSignIn = async () => {
    setIsSigningIn(true);
    try {
      await signIn();
      toast.success("Signed in successfully");
      onClose();
    } catch (e: any) {
      toast.error(e.message || "Sign in failed");
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please enter email and password");
      return;
    }
    
    setIsSigningIn(true);
    try {
      if (isSignUp) {
        await signUpWithEmail(email, password);
        toast.success("Account created successfully. A verification email has been sent.");
      } else {
        await signInWithEmail(email, password);
        toast.success("Signed in successfully");
      }
      onClose();
    } catch (error: any) {
      toast.error(error.message || "Authentication failed");
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Please enter your email address to reset your password");
      return;
    }
    
    setIsSigningIn(true);
    try {
      await resetPassword(email);
      toast.success("Password reset email sent. Check your inbox.");
    } catch (error: any) {
      toast.error(error.message || "Failed to send reset email");
    } finally {
      setIsSigningIn(false);
    }
  };

  return (
    <ResponsiveOverlay isOpen={isOpen} onClose={onClose} variant="modal" className="max-w-md w-full">
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Sign In to Resume Pro</h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          aria-label="Close Login Modal"
          className="rounded-lg text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      <div className="p-4 sm:p-6 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
        {isForgotPasswordMode ? (
          <div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 text-center leading-relaxed">
              Please enter your email address to reset your password
            </p>
            <form onSubmit={(e) => { e.preventDefault(); handleForgotPassword(); }} className="space-y-4 mb-6">
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 w-full h-9 sm:h-10 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:text-white transition-all outline-none"
                    placeholder="Email address"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isSigningIn}
                className="w-full h-9 sm:h-10 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer"
              >
                {isSigningIn ? (
                  <>
                     <Loader2 className="w-4 h-4 animate-spin" />
                     <span>Processing...</span>
                  </>
                ) : 'Send Reset Link'}
              </button>
            </form>
            <div className="mt-4 text-center">
              <button 
                type="button" 
                onClick={() => setIsForgotPasswordMode(false)}
                className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:underline focus:outline-none transition-colors cursor-pointer"
              >
                Back to Sign In
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6 text-center leading-relaxed">
              Create an account to get <strong className="text-emerald-600 dark:text-emerald-400">20 Free Credits</strong>.
              No credit card required.
            </p>

            <form onSubmit={handleEmailAuth} className="space-y-4 mb-6">
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 w-full h-9 sm:h-10 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:text-white transition-all outline-none"
                    placeholder="Email address"
                  />
                </div>
              </div>
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 w-full h-9 sm:h-10 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:text-white transition-all outline-none"
                    placeholder="Password"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isSigningIn}
                className="w-full h-9 sm:h-10 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition-colors disabled:opacity-50 cursor-pointer"
              >
                {isSigningIn ? (
                   <>
                     <Loader2 className="w-4 h-4 animate-spin" />
                     <span>Processing...</span>
                   </>
                ) : (isSignUp ? 'Create Account' : 'Sign In with Email')}
              </button>
            </form>

            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200 dark:border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-slate-900 text-slate-500">Or continue with</span>
              </div>
            </div>

            <button
              onClick={handleGoogleSignIn}
              disabled={isSigningIn}
              className="w-full h-9 sm:h-10 flex items-center justify-center gap-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition duration-150 disabled:opacity-50 cursor-pointer dark:shadow-sm"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              Google
            </button>
            
            <div className="mt-4 text-center flex flex-col gap-2">
              <button 
                type="button" 
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline focus:outline-none cursor-pointer"
              >
                {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
              </button>
              {!isSignUp && (
                <button 
                  type="button" 
                  onClick={() => setIsForgotPasswordMode(true)}
                  className="text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:underline focus:outline-none transition-colors cursor-pointer"
                >
                  Forgot your password?
                </button>
              )}
            </div>
          </>
        )}

        <p className="text-[11px] text-slate-500 mt-6 text-center leading-relaxed">
          By signing in, you agree to our <Link to="/terms" onClick={onClose} className="underline hover:text-slate-800 dark:hover:text-slate-300">Terms of Service</Link> and <Link to="/privacy" onClick={onClose} className="underline hover:text-slate-800 dark:hover:text-slate-300">Privacy Policy</Link>. <br/>
          <strong>We do not store your data.</strong> We respect your data protection rights under GDPR, CCPA, and other global data privacy laws. Our systems act simply as a secure stateless pass-through proxy.
        </p>
      </div>
    </ResponsiveOverlay>
  );
}
